import {
  DesignTokenSizeEnum,
  Text,
  TokenTextAppearance,
  TokenTextColor,
  TextTag,
} from '@volkswagen-onehub/components-core';
import { ReactNode, useState } from 'react';
import { useTribalContext } from '@/context/tribal';
import { useConfigurationSpecs } from '@/hooks/useConfigurationSpecs';
import { FeatureStatus, useFeatures } from '@/hooks/useFeatures';
import { WLTPModal } from '@/modules/features/components/WLTPModal/WLTPModal';
import {
  getBuildableConfigIndices,
  getBuildableConfigs,
  markNeverOnStockSpecs,
  mergeSpecs,
  updateSpecsWithNotAvailable,
} from '@/modules/utils/storeUtils';
import useGlobalStore from '@/stores/globalStore';
import {
  Accordion,
  AccordionGroup,
  AutoScrollContainer,
  Box,
  FeatureBox,
  SectionHeader,
} from '@/modules/components';
import { formatIdForDOM } from '@/modules/utils/dom';
import EmissionsTsAndCs from './EmissionsTsAndCs';
import EngineAndTransmissionTsAndCs from './EngineAndTransmissionTsAndCs';
import { SectionNames } from '@/analytics/sectionNames';
import {
  handleAccordionClose,
  handleAccordionOpen,
} from '@/analytics/eventHelpers/accordion';
import { DisclaimerText } from '@/styles';

interface SpecsCategory {
  title: string;
  specs: {
    label: string;
    values: string[];
  }[];
  termsAndConditions?: ReactNode;
  moreInfo?: ReactNode;
}

const ModelSpecifications = () => {
  const { contentManager, analyticsBuilder, analyticsManager } =
    useTribalContext();
  const { configurations, retailers } = useGlobalStore((state) => state.app);
  const activeSlideIndex = useGlobalStore((state) => state.activeSlideIndex);
  const { getSpecItemLabel } = useFeatures();

  const [isSpecDetailOpen, setIsSpecDetailOpen] = useState<
    Record<number, boolean>
  >({});

  const invalidSpecFallback = contentManager.get(
    'not-applicable-feature-label'
  );

  const validConfigurations = getBuildableConfigs(configurations);
  const validConfigurationIndices = getBuildableConfigIndices(configurations);

  const stockFlags = validConfigurations.map((configuration) =>
    Boolean(configuration.vin || configuration.orderNumber)
  );
  const stockFallbacks = stockFlags.map((isStock) =>
    isStock ? FeatureStatus.NOT_AVAILABLE : invalidSpecFallback
  );
  const neverOnStockPerformanceLabels = [
    contentManager.get('acceleration-label'),
    contentManager.get('maximum-speed-label'),
    contentManager.get('maximum-torque-label'),
  ];

  const analyticsCommonFields = {
    sectionName: SectionNames.COMPARE_PAGE_MODEL_SPECIFICATIONS,
    analyticsManager,
    analyticsBuilder,
    retailers,
    configurations,
  };

  const onToggleAccordion = (index: number, groupItemName: string) => {
    setIsSpecDetailOpen((prevState) => ({
      ...prevState,
      [index]: !prevState[index],
    }));

    if (!isSpecDetailOpen[index]) {
      handleAccordionOpen(analyticsCommonFields, {
        groupItemName,
        groupItemState: 'open',
      });
    } else {
      handleAccordionClose(analyticsCommonFields, {
        groupItemName,
        groupItemState: 'closed',
      });
    }
  };

  const getFeatureBoxProps = (value: string, invalidSpecFallback: string) => {
    if (value !== invalidSpecFallback) {
      if (value === FeatureStatus.NOT_AVAILABLE) {
        return {
          status: FeatureStatus.NOT_AVAILABLE,
          label: getSpecItemLabel(FeatureStatus.NOT_AVAILABLE),
        };
      } else {
        return {
          status: FeatureStatus.STANDARD,
          label: value,
        };
      }
    }

    return {
      status: FeatureStatus.NOT_INCLUDED,
      label: invalidSpecFallback,
    };
  };

  let engineAndTransmissionSpecs = mergeSpecs(
    validConfigurations.map((configuration) => {
      const { getEngineAndTransmissionSpecs } = useConfigurationSpecs({
        configuration,
        invalidSpecFallback,
      });
      // Stock cars have no Dimensions section, so doors sit here for every column.
      return getEngineAndTransmissionSpecs({ includeDoors: true });
    }),
    invalidSpecFallback
  );

  engineAndTransmissionSpecs = updateSpecsWithNotAvailable(
    engineAndTransmissionSpecs,
    validConfigurationIndices
  );

  let performanceSpecs = mergeSpecs(
    validConfigurations.map((configuration) => {
      const { getPerformanceSpecs } = useConfigurationSpecs({
        configuration,
        invalidSpecFallback,
      });
      return getPerformanceSpecs();
    }),
    invalidSpecFallback
  );

  performanceSpecs = markNeverOnStockSpecs(
    performanceSpecs,
    stockFlags,
    invalidSpecFallback,
    neverOnStockPerformanceLabels
  );

  performanceSpecs = updateSpecsWithNotAvailable(
    performanceSpecs,
    validConfigurationIndices
  );


  let dimensionsSpecs = mergeSpecs(
    validConfigurations.map((configuration) => {
      const { getDimensionsSpecs } = useConfigurationSpecs({
        configuration,
        invalidSpecFallback,
      });
      return getDimensionsSpecs();
    }),
    stockFallbacks
  );

  dimensionsSpecs = updateSpecsWithNotAvailable(
    dimensionsSpecs,
    validConfigurationIndices
  );

  let loadingSpecs = mergeSpecs(
    validConfigurations.map((configuration) => {
      const { getLoadingSpecs } = useConfigurationSpecs({
        configuration,
        invalidSpecFallback,
      });
      return getLoadingSpecs();
    }),
    stockFallbacks
  );

  loadingSpecs = updateSpecsWithNotAvailable(
    loadingSpecs,
    validConfigurationIndices
  );

  let fuelConsumptionSpecs = mergeSpecs(
    validConfigurations.map((configuration) => {
      const { getFuelConsumptionSpecs } = useConfigurationSpecs({
        configuration,
        invalidSpecFallback,
      });
      return getFuelConsumptionSpecs();
    }),
    invalidSpecFallback
  );

  fuelConsumptionSpecs = updateSpecsWithNotAvailable(
    fuelConsumptionSpecs,
    validConfigurationIndices
  );

  let energyConsumptionSpecs = mergeSpecs(
    validConfigurations.map((configuration) => {
      const { getEnergyConsumptionSpecs } = useConfigurationSpecs({
        configuration,
        invalidSpecFallback,
      });
      return getEnergyConsumptionSpecs();
    }),
    invalidSpecFallback
  );

  energyConsumptionSpecs = updateSpecsWithNotAvailable(
    energyConsumptionSpecs,
    validConfigurationIndices
  );

  let batteryRangeSpecs = mergeSpecs(
    validConfigurations.map((configuration) => {
      const { getBatteryRangeSpecs } = useConfigurationSpecs({
        configuration,
        invalidSpecFallback,
      });
      return getBatteryRangeSpecs();
    }),
    invalidSpecFallback
  );

  batteryRangeSpecs = updateSpecsWithNotAvailable(
    batteryRangeSpecs,
    validConfigurationIndices
  );

  let c02EmissionsSpecs = mergeSpecs(
    validConfigurations.map((configuration) => {
      const { getEmissionsCombinedSpecs } = useConfigurationSpecs({
        configuration,
        invalidSpecFallback,
      });
      return getEmissionsCombinedSpecs();
    }),
    invalidSpecFallback
  );

  c02EmissionsSpecs = updateSpecsWithNotAvailable(
    c02EmissionsSpecs,
    validConfigurationIndices
  );

  const MoreInfoWLTPModal = () => (
    <WLTPModal
      sectionName={SectionNames.COMPARE_PAGE_MODEL_SPECIFICATIONS}
      configurations={configurations}
    />
  );

  const modelSpecifications: Array<SpecsCategory> = [
    {
      title: contentManager.get('specifications-engine-title'),
      specs: engineAndTransmissionSpecs,
      termsAndConditions: (
        <EngineAndTransmissionTsAndCs configurations={configurations} />
      ),
    },
    {
      title: contentManager.get('specifications-performance-title'),
      specs: performanceSpecs,
    },
    {
      title: contentManager.get('specifications-dimensions-title'),
      specs: dimensionsSpecs,
    },
    {
      title: 'Loading',
      specs: loadingSpecs,
    },
    {
      title: contentManager.get('specifications-fuel-consumption-title'),
      specs: fuelConsumptionSpecs,
      moreInfo: <MoreInfoWLTPModal />,
    },
    {
      title: contentManager.get('specifications-energy-consumption-title'),
      specs: energyConsumptionSpecs,
      moreInfo: <MoreInfoWLTPModal />,
    },
    {
      title: contentManager.get('specifications-electric-battery-range-title'),
      specs: batteryRangeSpecs,
      moreInfo: <MoreInfoWLTPModal />,
    },
    {
      title: contentManager.get('specifications-emissions-title'),
      specs: c02EmissionsSpecs,
      moreInfo: <MoreInfoWLTPModal />,
    },
  ];

  const filteredModelSpecifications = modelSpecifications.filter(
    ({ specs }) => !!specs.length
  );

  return (
    <div>
      <Box
        as={SectionHeader}
        title=""
        titleBold={`${contentManager.get('model-specifications-section-title-normal')} ${contentManager.get('model-specifications-section-title-bold')}`}
        titleSize={TokenTextAppearance.headline400}
        description={contentManager.get(
          'model-specifications-section-description'
        )}
        descriptionSize={TokenTextAppearance.bigcopy100}
        mb={DesignTokenSizeEnum.static370}
        b960={{
          mb: DesignTokenSizeEnum.static530,
        }}
      />
      <Box mb={DesignTokenSizeEnum.static515}>
        <AccordionGroup>
          {filteredModelSpecifications.map(
            ({ title, specs, moreInfo, termsAndConditions }, index) => {
              return (
                <Accordion
                  title={
                    <Box display="flex" gap="static100">
                      <span>{title}</span>
                      {moreInfo}
                    </Box>
                  }
                  titleAppearance={TokenTextAppearance.copy200}
                  id={formatIdForDOM(title)}
                  key={formatIdForDOM(title)}
                  isOpen={isSpecDetailOpen[index]}
                  onToggle={() => onToggleAccordion(index, title)}
                >
                  {specs.map(({ label, values }) => (
                    <Box mb="static400" key={label}>
                      <Box mb="static150" display="flex" gap="static100">
                        <Box
                          as={Text}
                          tag={TextTag.h4}
                          appearance={TokenTextAppearance.copy200}
                          bold
                          m="static0"
                        >
                          {label}
                        </Box>
                      </Box>

                      <AutoScrollContainer activeSlideIndex={activeSlideIndex}>
                        {values.map((value: string, index: number) => {
                          const { status, label } = getFeatureBoxProps(
                            value,
                            invalidSpecFallback
                          );

                          return (
                            <FeatureBox status={status} key={index}>
                              {label}
                            </FeatureBox>
                          );
                        })}
                      </AutoScrollContainer>
                      {termsAndConditions && (
                        <DisclaimerText>
                          <Text
                            tag={TextTag.div}
                            color={TokenTextColor.inherit}
                            appearance={TokenTextAppearance.copy100}
                          >
                            {termsAndConditions}
                          </Text>
                        </DisclaimerText>
                      )}
                    </Box>
                  ))}
                </Accordion>
              );
            }
          )}
          <DisclaimerText appearance={TokenTextAppearance.copy100}>
            {validConfigurations.map((configuration) => (
              <EmissionsTsAndCs
                key={configuration.pdbData?.derivative?.trimId}
                configuration={configuration}
              />
            ))}
          </DisclaimerText>
        </AccordionGroup>
      </Box>
    </div>
  );
};

export default ModelSpecifications;
